import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { navApi } from "../../lib/api";
import { MessageCircle, X, Send, ArrowUpRight, Phone, Calendar, Sparkles } from "lucide-react";
import axios from "axios";

const STORAGE_KEY = "cod_public_nav_v2";
const MAX_HISTORY = 10;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const genSessionId = () =>
  "pub-" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

const loadSession = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.session_id && Array.isArray(parsed.messages)) return parsed;
    }
  } catch { /* noop */ }
  return { session_id: genSessionId(), messages: [], lead: null };
};

const saveSession = (session) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(session)); }
  catch { /* noop */ }
};

// Lightweight analytics — pushes to window.gtag / dataLayer if present; safe no-op otherwise.
const track = (event, params = {}) => {
  try {
    if (window.gtag) window.gtag("event", event, params);
    if (window.dataLayer) window.dataLayer.push({ event, ...params });
  } catch { /* noop */ }
};

// SSE stream via fetch + ReadableStream (EventSource can't POST).
async function streamPublicChat({ session_id, message, history, onDelta, onDone, onError }) {
  try {
    const res = await fetch(`${API}/navigator/public/stream`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "text/event-stream" },
      body: JSON.stringify({ session_id, message, history }),
    });
    if (!res.ok || !res.body) {
      onError?.(new Error(`HTTP ${res.status}`)); return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split("\n\n");
      buffer = events.pop() || "";
      for (const raw of events) {
        const line = raw.trim();
        if (!line.startsWith("data:")) continue;
        try {
          const payload = JSON.parse(line.slice(5).trim());
          if (payload.delta) onDelta?.(payload.delta);
          if (payload.done) { onDone?.(); return; }
        } catch { /* skip malformed */ }
      }
    }
    onDone?.();
  } catch (e) { onError?.(e); }
}

export default function PublicChatWidget() {
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(loadSession);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("chat"); // "chat" | "lead" | "post-lead"
  const [leadForm, setLeadForm] = useState({
    name: "", email: "", phone: "", needs: "", preferred_contact: "email",
  });
  const [leadPending, setLeadPending] = useState(false);
  const [leadDone, setLeadDone] = useState(null); // { action } after success
  const scrollRef = useRef(null);

  useEffect(() => { saveSession(session); }, [session]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [session.messages, busy, mode]);

  const messages = session.messages;
  const messageCount = messages.filter(m => m.role === "user").length;

  const openWidget = () => {
    setOpen(true);
    track("chat_opened", { session_id: session.session_id });
  };

  const send = async (text, meta = {}) => {
    const clean = (text ?? input).trim();
    if (!clean || busy) return;
    track("message_submitted", {
      session_id: session.session_id,
      length: clean.length,
      source: meta.source || "input",
    });
    const nextMessages = [...messages, { role: "user", text: clean, ts: Date.now() }];
    setSession(s => ({ ...s, messages: nextMessages }));
    setInput("");
    setBusy(true);

    // Placeholder assistant message that will fill as tokens stream in
    const placeholder = { role: "assistant", text: "", ts: Date.now(), streaming: true };
    setSession(s => ({ ...s, messages: [...nextMessages, placeholder] }));

    const history = nextMessages.slice(-MAX_HISTORY, -1);
    await streamPublicChat({
      session_id: session.session_id,
      message: clean,
      history,
      onDelta: (delta) => {
        setSession(s => {
          const msgs = [...s.messages];
          const last = msgs[msgs.length - 1];
          if (last && last.streaming) last.text += delta;
          return { ...s, messages: msgs };
        });
      },
      onDone: () => {
        setSession(s => {
          const msgs = [...s.messages];
          const last = msgs[msgs.length - 1];
          if (last) delete last.streaming;
          return { ...s, messages: msgs };
        });
        setBusy(false);
      },
      onError: () => {
        setSession(s => {
          const msgs = [...s.messages];
          const last = msgs[msgs.length - 1];
          if (last && last.streaming) {
            last.text = last.text || "I couldn't reach the Navigator. Try again, or call (312) 343-4183.";
            last.error = true;
            delete last.streaming;
          }
          return { ...s, messages: msgs };
        });
        setBusy(false);
      },
    });
  };

  const resetChat = () => {
    const fresh = { session_id: genSessionId(), messages: [], lead: null };
    setSession(fresh);
    saveSession(fresh);
    setMode("chat"); setLeadDone(null);
    setLeadForm({ name: "", email: "", phone: "", needs: "", preferred_contact: "email" });
  };

  const openLeadForm = (action) => {
    track("appointment_request_started", { session_id: session.session_id, action });
    // Pre-fill needs from the conversation if empty
    const inferredNeeds = leadForm.needs || messages.filter(m => m.role === "user").map(m => m.text).slice(-3).join(" | ");
    setLeadForm(f => ({ ...f, needs: inferredNeeds, _action: action }));
    setMode("lead");
  };

  const submitLead = async (e) => {
    e?.preventDefault?.();
    if (leadPending) return;
    setLeadPending(true);
    try {
      const res = await axios.post(`${API}/navigator/public/lead`, {
        session_id: session.session_id,
        name: leadForm.name.trim(),
        email: leadForm.email.trim(),
        phone: leadForm.phone.trim() || null,
        needs: leadForm.needs.trim(),
        preferred_contact: leadForm.preferred_contact,
        action: leadForm._action || "plan",
        context: messages.slice(-12),
      });
      // Persist lead so signup flow can transfer it as Mode-2 context
      const savedLead = {
        name: leadForm.name, email: leadForm.email, phone: leadForm.phone,
        needs: leadForm.needs, preferred_contact: leadForm.preferred_contact,
        action: leadForm._action, lead_id: res.data.lead_id, saved_at: Date.now(),
      };
      setSession(s => ({ ...s, lead: savedLead }));
      setLeadDone({ action: leadForm._action });
      setMode("post-lead");
      track("appointment_request_completed", {
        session_id: session.session_id,
        action: leadForm._action,
        preferred_contact: leadForm.preferred_contact,
      });
    } catch (err) {
      alert(err?.response?.data?.detail || "Could not send your request. Please try again.");
    } finally { setLeadPending(false); }
  };

  const quickPrompts = useMemo(() => [
    "What does COD do?",
    "How long is CNA training?",
    "Do I qualify?",
    "How do I sign up?",
  ], []);

  const trackUpgradeClick = () => {
    track("upgrade_cta_clicked", { session_id: session.session_id, message_count: messageCount });
  };

  const showActionsBar = messageCount >= 2 && mode === "chat";

  return (
    <>
      {!open && (
        <button
          onClick={openWidget}
          className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full px-5 py-3 shadow-lg transition-transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-offset-2"
          style={{ background: "var(--cod-red)", color: "#fff", boxShadow: "0 10px 24px rgba(200,16,46,0.35)" }}
          aria-label="Open the Chicago Opportunity Desk chat"
          data-testid="public-chat-open"
        >
          <MessageCircle size={18} strokeWidth={2.5}/>
          <span className="text-sm font-semibold">Ask COD</span>
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-5 right-5 z-40 flex flex-col w-[calc(100vw-2.5rem)] sm:w-[400px] h-[620px] max-h-[88vh] rounded-xl overflow-hidden bg-white border border-slate-200"
          style={{ boxShadow: "0 20px 48px rgba(15,23,42,0.18)" }}
          role="dialog"
          aria-label="Chicago Opportunity Desk quick-answer chat"
          data-testid="public-chat-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200" style={{ background: "var(--cod-navy)" }}>
            <div className="text-white">
              <div className="font-serif text-base leading-tight">Chicago Opportunity Desk</div>
              <div className="text-[11px] opacity-80 tracking-wide uppercase">Quick-answer Navigator</div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/85 hover:text-white p-1 rounded-md" aria-label="Close chat" data-testid="public-chat-close">
              <X size={18}/>
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50">
            {mode === "chat" && messages.length === 0 && (
              <div className="text-sm text-slate-600">
                <p className="mb-2 text-slate-800 font-medium">Hi — I'm the COD quick-answer Navigator.</p>
                <p className="text-slate-600 mb-3">Ask about services, certifications, pathways, hours, or how to sign up. Answers stream in a few seconds.</p>
                <div className="grid gap-2">
                  {quickPrompts.map(q => (
                    <button
                      key={q}
                      onClick={() => {
                        track("quick_prompt_clicked", { prompt: q, session_id: session.session_id });
                        send(q, { source: "quick_prompt" });
                      }}
                      className="text-left text-sm px-3 py-2 rounded-md border border-slate-200 bg-white hover:border-[var(--cod-navy)] hover:bg-slate-50 transition-colors"
                      data-testid={`public-chat-prompt-${q.slice(0,10)}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {mode === "chat" && messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div
                  className={m.role === "user"
                    ? "max-w-[85%] rounded-2xl rounded-br-sm px-3.5 py-2.5 text-sm text-white"
                    : `max-w-[92%] rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-sm text-slate-800 border ${m.error ? "border-rose-200 bg-rose-50" : "border-slate-200 bg-white"}`}
                  style={m.role === "user" ? { background: "var(--cod-navy)" } : undefined}
                >
                  <div className="whitespace-pre-wrap leading-relaxed">
                    {m.text}
                    {m.streaming && <span className="inline-block ml-1 align-middle w-1.5 h-3.5" style={{ background: "var(--cod-navy)", animation: "cod-blink 1s steps(2) infinite" }}/>}
                  </div>
                </div>
              </div>
            ))}

            {mode === "lead" && (
              <form onSubmit={submitLead} className="space-y-2.5 bg-white border border-slate-200 rounded-lg p-4" data-testid="public-lead-form">
                <div className="flex items-center gap-2 mb-1"><Sparkles size={16} style={{ color: "var(--cod-red)" }}/><span className="text-sm font-semibold text-slate-900">Let's connect you with COD</span></div>
                <p className="text-xs text-slate-500 mb-2">A COD team member will reach out. Nothing about your situation is stored publicly.</p>
                <input required placeholder="Your name" value={leadForm.name} onChange={e=>setLeadForm(f=>({...f,name:e.target.value}))} className="w-full text-sm px-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:border-[var(--cod-navy)]" data-testid="lead-name"/>
                <input required type="email" placeholder="Email" value={leadForm.email} onChange={e=>setLeadForm(f=>({...f,email:e.target.value}))} className="w-full text-sm px-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:border-[var(--cod-navy)]" data-testid="lead-email"/>
                <input type="tel" placeholder="Phone (optional)" value={leadForm.phone} onChange={e=>setLeadForm(f=>({...f,phone:e.target.value}))} className="w-full text-sm px-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:border-[var(--cod-navy)]" data-testid="lead-phone"/>
                <textarea required placeholder="What do you need help with?" rows={3} value={leadForm.needs} onChange={e=>setLeadForm(f=>({...f,needs:e.target.value}))} className="w-full text-sm px-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:border-[var(--cod-navy)]" data-testid="lead-needs"/>
                <div>
                  <label className="text-xs text-slate-600">Preferred contact method</label>
                  <div className="mt-1 grid grid-cols-3 gap-1.5">
                    {["email","phone","text"].map(pc => (
                      <button type="button" key={pc}
                        onClick={()=>setLeadForm(f=>({...f,preferred_contact:pc}))}
                        className={`text-xs py-1.5 rounded-md border ${leadForm.preferred_contact===pc ? "border-[var(--cod-navy)] bg-[var(--cod-navy)] text-white" : "border-slate-200 text-slate-700 bg-white"}`}
                        data-testid={`lead-contact-${pc}`}
                      >{pc[0].toUpperCase()+pc.slice(1)}</button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 pt-1">
                  <button type="button" onClick={()=>setMode("chat")} className="text-xs px-3 py-1.5 rounded-md border border-slate-200 text-slate-700" data-testid="lead-cancel">Back</button>
                  <button type="submit" disabled={leadPending} className="flex-1 text-sm font-semibold px-3 py-2 rounded-md text-white disabled:opacity-60" style={{ background: "var(--cod-red)" }} data-testid="lead-submit">
                    {leadPending ? "Sending…" : "Send to COD"}
                  </button>
                </div>
              </form>
            )}

            {mode === "post-lead" && leadDone && (
              <div className="space-y-3 bg-white border border-slate-200 rounded-lg p-4" data-testid="public-lead-done">
                <div className="text-sm">
                  <div className="font-semibold text-slate-900 mb-1">Got it — you're on our radar.</div>
                  <p className="text-slate-600 text-sm">A COD team member will follow up by your preferred contact method within one business day.</p>
                </div>
                <Link
                  to={`/start?ctx=${session.session_id}`}
                  onClick={()=>{ trackUpgradeClick(); setOpen(false); }}
                  className="block text-center text-sm font-semibold px-3 py-2.5 rounded-md text-white"
                  style={{ background: "var(--cod-red)" }}
                  data-testid="post-lead-create-account"
                >
                  Create your free account →
                </Link>
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  Creating an account allows us to keep track of your progress so you never have to start over.
                </p>
              </div>
            )}
          </div>

          {showActionsBar && (
            <div className="px-3 py-2 border-t border-slate-200 bg-white">
              <div className="text-[11px] text-slate-500 mb-1.5">Ready to take the next step?</div>
              <p className="text-[11px] text-slate-600 mb-2 leading-relaxed">
                Create your free account so we can build your Individual Opportunity Plan, keep track of your progress, remember where you left off, and guide you toward your goals.
              </p>
              <div className="grid grid-cols-3 gap-1.5">
                <button onClick={()=>openLeadForm("appointment")} className="text-[11px] font-semibold px-2 py-1.5 rounded-md border flex items-center justify-center gap-1" style={{ color: "var(--cod-navy)", borderColor: "var(--cod-navy)" }} data-testid="action-appointment">
                  <Calendar size={12}/> Book
                </button>
                <Link to={`/start?ctx=${session.session_id}`} onClick={()=>{ trackUpgradeClick(); setOpen(false); }} className="text-[11px] font-semibold px-2 py-1.5 rounded-md text-white flex items-center justify-center gap-1" style={{ background: "var(--cod-red)" }} data-testid="action-plan">
                  <Sparkles size={12}/> Create Plan
                </Link>
                <a href="tel:+13123434183" onClick={()=>track("call_cod_clicked",{ session_id: session.session_id })} className="text-[11px] font-semibold px-2 py-1.5 rounded-md border flex items-center justify-center gap-1" style={{ color: "var(--cod-navy)", borderColor: "var(--cod-navy)" }} data-testid="action-call">
                  <Phone size={12}/> Call
                </a>
              </div>
              <p className="text-[10px] text-slate-400 mt-1.5 leading-relaxed">
                Creating an account allows us to keep track of your progress so you never have to start over.
              </p>
            </div>
          )}

          {mode === "chat" && (
            <form className="border-t border-slate-200 bg-white p-3 flex items-center gap-2" onSubmit={(e)=>{ e.preventDefault(); send(); }}>
              <input type="text" value={input} onChange={(e)=>setInput(e.target.value)}
                placeholder={busy ? "Streaming…" : "Ask a quick question…"} disabled={busy}
                className="flex-1 text-sm px-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:border-[var(--cod-navy)]"
                data-testid="public-chat-input" autoFocus maxLength={1500} aria-label="Type your question"/>
              <button type="submit" disabled={!input.trim() || busy} className="rounded-md p-2 disabled:opacity-40" style={{ background: "var(--cod-red)", color: "#fff" }} aria-label="Send" data-testid="public-chat-send">
                <Send size={16}/>
              </button>
            </form>
          )}

          <div className="px-3 pb-2 text-[10px] text-slate-400 bg-white flex items-center justify-between">
            <span>AI · quick answers only · not a replacement for a Case Manager</span>
            {messages.length > 0 && mode === "chat" && (
              <button onClick={resetChat} className="underline hover:text-slate-600" data-testid="public-chat-reset">Reset</button>
            )}
          </div>

          <style>{`@keyframes cod-blink { 0%,50%{opacity:1} 51%,100%{opacity:0} }`}</style>
        </div>
      )}
    </>
  );
}
